/**
 * uploadLimits — Envío AP-2 · reglas puras de la subida de archivos.
 *
 * Sin red ni React: decide la vía (directa o clásica), arma los mensajes en
 * español y traduce los errores del servidor. uploadToR2 las usa.
 */
export const MB = 1024 * 1024;
// Por debajo de esto la subida clásica (un solo pedido) es más rápida.
export const DIRECT_MIN_BYTES = 8 * MB;
export const DEFAULT_MULTIPART_MAX_MB = 50;

const KIND_LABEL = { image: 'imagen', video: 'video', audio: 'audio' };

export class UploadError extends Error {
  constructor(code, userMessage, { status = null, cause = null } = {}) {
    super(userMessage);
    this.name = 'UploadError';
    this.code = code;
    this.userMessage = userMessage;
    this.status = status;
    this.cause = cause;
  }
}

export function kindOf(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return null;
}

export function formatMB(bytes) {
  const value = (Number(bytes) || 0) / MB;
  const text = value < 100 ? value.toFixed(1) : value.toFixed(0);
  return `${text.replace('.', ',').replace(/,0$/, '')} MB`;
}

export function limitMB(limits, kind) {
  const mb = limits?.limits_mb?.[kind];
  return Number.isFinite(mb) && mb > 0 ? mb : null;
}

export function multipartMaxBytes(limits) {
  const mb = limits?.multipart_max_mb;
  return (Number.isFinite(mb) && mb > 0 ? mb : DEFAULT_MULTIPART_MAX_MB) * MB;
}

export function tooLargeMessage(size, limits, kind) {
  const mb = limitMB(limits, kind);
  const plan = limits?.plan_label ? ` ${limits.plan_label}` : '';
  return `El archivo pesa ${formatMB(size)} y tu plan${plan} permite hasta ${mb} MB por ${KIND_LABEL[kind] || 'archivo'}.`;
}

/** Valida antes de subir. Devuelve un UploadError o null. */
export function precheck(file, limits) {
  const kind = kindOf(file);
  if (!kind) {
    return new UploadError('unsupported', 'Tipo de archivo no admitido. Sólo imágenes, videos y audios.');
  }
  const mb = limitMB(limits, kind);
  if (mb && file.size > mb * MB) {
    return new UploadError('too_large', tooLargeMessage(file.size, limits, kind));
  }
  return null;
}

/**
 * 'direct' | 'multipart' | 'unavailable'.
 * Sin datos de límites (servidor viejo) se usa la vía clásica de siempre.
 */
export function chooseRoute(file, limits, { directBroken = false } = {}) {
  const multipartMax = multipartMaxBytes(limits);
  const directOk = !!limits && limits.direct_upload !== false && !directBroken;
  if (directOk && file.size > DIRECT_MIN_BYTES) return 'direct';
  if (file.size <= multipartMax) return 'multipart';
  return directOk ? 'direct' : 'unavailable';
}

export function unavailableMessage(file, limits) {
  const max = Math.round(multipartMaxBytes(limits) / MB);
  return `El archivo pesa ${formatMB(file.size)} y ahora sólo se pueden subir archivos de hasta ${max} MB (la subida directa no está disponible). Avisale al administrador.`;
}

/** Traduce un error de axios / XHR a un UploadError con mensaje claro. */
export function toUploadError(err) {
  if (err instanceof UploadError) return err;
  const status = err?.response?.status ?? null;
  const raw = err?.response?.data?.detail;
  const detail = typeof raw === 'string' ? raw : null;
  if (status === 413) return new UploadError('too_large', detail || 'El archivo es demasiado grande.', { status, cause: err });
  if (status === 415) return new UploadError('unsupported', detail || 'Tipo de archivo no admitido.', { status, cause: err });
  if (status === 429) return new UploadError('rate_limited', detail || 'Demasiadas subidas seguidas. Probá en un rato.', { status, cause: err });
  if (status === 401) return new UploadError('auth', 'Tu sesión venció. Volvé a iniciar sesión y probá de nuevo.', { status, cause: err });
  if (err?.code === 'ECONNABORTED') {
    return new UploadError('timeout', 'La subida tardó demasiado y se cortó. Revisá tu conexión y probá de nuevo.', { cause: err });
  }
  if (!err?.response && status === null) {
    return new UploadError('network', 'Se cortó la conexión durante la subida. Probá de nuevo.', { cause: err });
  }
  return new UploadError('failed', detail || 'No se pudo subir el archivo. Probá de nuevo.', { status, cause: err });
}

export function describeUploadError(err) {
  return toUploadError(err).userMessage;
}
